import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, concat, defer, of } from 'rxjs';
import {
  catchError,
  first,
  map,
  switchMap,
  takeUntil,
  toArray,
} from 'rxjs/operators';
import { DcFileUploadComponent } from 'src/app/ui-kit/dc-file-upload/dc-file-upload.component';
import { DcAlertService } from "../../ui-kit/dc-alert/dc-alert.service";
import { MODAL_CUSTOM_DATA } from '../../utils/create-modal-injector';
import { ProductCaptureData } from '../model/interfaces/product-capture.interface';
import { FileDetails } from './import-product.consts';
import { ImportProductService } from './import-product.service';

@Component({
  selector: 'app-import-product',
  templateUrl: './import-product.component.html',
  styleUrls: ['./import-product.component.scss'],
})
export class ImportProductComponent implements OnInit {
  
  @ViewChild(DcFileUploadComponent, { static: true })
  fileUploader: DcFileUploadComponent;

  processDone = false;

  destroy$ = new Subject<boolean>();

  productsImported = [];

  constructor(
    public modal: NgbActiveModal,
    private importProductService: ImportProductService,
    private dcAlertService: DcAlertService,
    @Inject(MODAL_CUSTOM_DATA)
    @Optional()
    public data: { isEdit: true; product: ProductCaptureData }
  ) {}

  ngOnInit(): void {}

  importFiles(): void {
    this.processDone = false;
    this.productsImported = [];
    const errors = [];

    this.fileUploader
      .startQueue()
      .pipe(
        switchMap((results: FileDetails[]) => {
          const parsed$ = results.reduce(
            (
              acc: Observable<{ type: string; content: any }>[],
              current: FileDetails
            ) => {
              let parsedContent = null;
              try {
                if (current.file?.type === 'application/json') {
                  parsedContent = JSON.parse(current.content);
                } else {
                  parsedContent = current.content;
                }
                acc.push(
                  of({ type: current.file.type, content: parsedContent })
                );
              } catch (err) {
                current.error = `Error trying to read file: ${err}`;
              }
              return acc;
            },
            []
          );

          return concat(...parsed$).pipe(
            toArray(),
            switchMap((fileContents: { type: string; content: any }[]) => {
              const processes$ = fileContents.reduce(
                (
                  acc: Observable<any>[],
                  current: { type: string; content: any },
                  index: number
                ) => {
                  const detail = results[index];

                  acc.push(
                    defer(() => {
                      detail.processing = true;

                      const request = this.data.isEdit
                        ? this.importProductService.updateFile(
                          current.type as
                            | 'text/xml'
                            | 'application/json',
                            this.data.product.id,
                            current.content
                          )
                        : this.importProductService.processFile(
                            current.type as
                              | 'text/xml'
                              | 'text/csv'
                              | 'application/json',
                            current.content
                          );

                      return request.pipe(
                        map((result: boolean) => {
                          detail.processing = false;
                          detail.processed = result;
                          detail.progress = detail.file.size;
                          this.productsImported.push(detail);

                          return result;
                        }),
                        catchError((err: HttpErrorResponse) => {
                          detail.error = err.error;
                          detail.processing = false;
                          detail.progress = detail.file.size;
                          errors.push(detail);

                          return of(err.error);
                        })
                      );
                    }).pipe(first())
                  );

                  return acc;
                },
                []
              );

              return concat(...processes$).pipe(toArray());
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.processDone = true;

        if (this.productsImported.length === this.fileUploader.files.length) {
          setTimeout(() => this.close(), 950);
        }
      },
        (error) => {
        this.dcAlertService.showAlert({
          type: 'danger',
          message: `Failed to update product: ${error.message}`,
        });
        console.log(error);
        });
  }

  close(): void {
    this.fileUploader.destroy$.next(true);
    this.destroy$.next(true);
    this.destroy$.complete();
    this.modal.close(this.productsImported?.length ? 'success' : 'close');
  }
}
